<template>
    <div v-loading="isLoading">
        <div class="row">
            <div class="col-md-9">
                <h3 class="text-dark">
                    <i class="el-icon-s-operation"></i>
                    <span>Configure</span>
                </h3>

                <p>
                    <small>
                        Configure your agent's basic settings, communication preferences, and AI model parameters.
                        These settings define how your agent behaves, communicates, and processes information
                        during interactions with contacts.
                    </small>
                </p>

                <!-- Inbound Lines Section - Only shown for existing inbound bots -->
                <div v-if="shouldShowInboundLinesSection" class="inbound-lines-section mb-4">
                    <span class="page-anchor" id="inbound_lines" />
                    <h4 class="mb-3 _500">
                        <i class="fa fa-phone-square mr-2"></i>
                        Inbound Lines
                    </h4>
                    <p class="mb-3">
                        <small>
                            These are the lines currently routing {{ bot.type === AloAi.TYPE_VOICE ? 'calls' : 'messages' }} to this agent.
                            Click on a line to configure its routing settings.
                        </small>
                    </p>

                    <!-- Lines List -->
                    <div v-if="connectedInboundLines.length > 0" class="lines-list mb-3">
                        <div 
                            v-for="line in connectedInboundLines" 
                            :key="line.id" 
                            class="line-item"
                            :data-testid="'aloai-inbound-line-item-' + line.id"
                        >
                            <div class="line-info">
                                <div class="line-name">
                                    <i class="fa fa-phone mr-2 text-muted"></i>
                                    {{ line.name }}
                                </div>
                                <div v-if="getLinePhoneNumber(line)" class="line-number text-muted">
                                    {{ getLinePhoneNumber(line) | fixPhone }}
                                </div>
                            </div>
                            <router-link 
                                :to="{ 
                                    name: 'Line Settings', 
                                    params: { campaign_id: line.id }, 
                                    query: { tab: 'routing-settings' }
                                }"
                                class="btn btn-sm btn-outline-primary go-to-line-btn"
                                :data-testid="'aloai-go-to-line-' + line.id"
                            >
                                <i class="fa fa-external-link mr-1"></i>
                                Go to the Line
                            </router-link>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else class="empty-state mb-3">
                        <i class="fa fa-info-circle mr-2"></i>
                        <span>No inbound lines are connected to this agent. Add a line to start routing {{ bot.type === AloAi.TYPE_VOICE ? 'calls' : 'messages' }} to this agent.</span>
                    </div>

                    <!-- Add Line Button -->
                    <el-button 
                        type="primary" 
                        size="small" 
                        @click="scrollToInboundLinesSelector"
                        data-testid="aloai-add-line-btn"
                    >
                        <i class="fa fa-plus mr-1"></i>
                        Add Line
                    </el-button>
                </div>

                <!-- Basic Information for all bots -->
                <span class="page-anchor" id="basic_information" />
                <h4 class="mb-4 _500">Basic Information</h4>

                <el-form-item prop="name">
                    <span class="page-anchor" id="name" />
                    <div class="form-label">
                        <h5>Name</h5>
                        <small>What name would you like to give to this agent?</small>
                    </div>
                    <el-input
                        v-model="bot.name"
                        data-testid="aloai-agent-name-input"
                        @input="$emit('validate')"
                    />
                </el-form-item>

                <el-form-item>
                    <div class="form-label">
                        <h5>Description</h5>
                        <small>Describe the purpose of this agent.</small>
                    </div>
                    <!-- Show as text for Call Rescue bots -->
                    <div v-if="bot.internal_type === AloAi.INTERNAL_TYPE_MISSED_CALL_HANDLER" class="description-text">
                        {{ bot.description }}
                    </div>
                    <!-- Show as textarea for other bots -->
                    <el-input
                        v-else
                        type="textarea"
                        :rows="2"
                        v-model="bot.description"
                        data-testid="aloai-agent-description-input"
                    />
                </el-form-item>

                <div v-if="bot.internal_type !== AloAi.INTERNAL_TYPE_MISSED_CALL_HANDLER">
                    <span class="page-anchor" id="communication_settings" />

                    <h4 class="mb-4 _500">Communication Settings</h4>

                    <el-form-item>
                        <div class="form-label">
                            <h5>Agent type</h5>
                            <small>
                                What is the main channel of communication for this agent?
                            </small>
                        </div>

                        <el-tooltip
                            :disabled="!blockAgentTypeChange"
                            placement="left"
                            content="Agent type cannot be changed after creation."
                        >
                            <el-select
                                class="w-100"
                                v-model="bot.type"
                                data-testid="aloai-agent-type-select"
                                :disabled="blockAgentTypeChange"
                                @change="handleBotTypeChange"
                            >
                        <el-option
                            v-for="type in types"
                            :key="type.value"
                            :label="type.name"
                            :value="type.value"
                            :disabled="lockTypeOption(type)"
                            :data-testid="`aloai-agent-type-option-${type.value}`"
                        />
                            </el-select>
                        </el-tooltip>
                    </el-form-item>

                    <el-form-item>
                        <div class="form-label">
                            <h5>Direction</h5>
                            <small>
                                Will this agent start the conversations or wait for your
                                contacts to reach out first?
                            </small>
                        </div>
                        <el-select
                            v-model="bot.direction"
                            data-testid="aloai-agent-direction-select"
                            class="w-100"
                            @change="handleBotDirectionChange"
                        >
                            <el-option
                                v-for="direction in directions"
                                :key="direction.value"
                                :label="direction.name"
                                :value="direction.value"
                                :disabled="lockDirectionOption(bot.type)"
                                :data-testid="`aloai-agent-direction-option-${direction.value}`"
                            />
                        </el-select>
                    </el-form-item>
                </div>

                <el-form-item
                    v-if="bot.direction == AloAi.DIRECTION_OUTBOUND"
                    prop="default_outbound_campaign_id"
                >
                    <div class="form-label">
                        <h5>Default outbound line</h5>
                    </div>
                    <el-radio-group
                        :value="defaultOutboundType"
                        data-testid="aloai-agent-outbound-type-radio-group"
                        @input="$emit('outbound-type-change', $event)"
                    >
                        <el-radio
                            v-for="option in outbound_types"
                            :key="option.value"
                            :label="option.value"
                            class="w-100"
                            :data-testid="`aloai-agent-outbound-type-radio-${option.value}`"
                        >
                            {{ option.name }}
                        </el-radio>
                    </el-radio-group>
                    <el-select
                        v-if="defaultOutboundType == AloAi.OUTBOUND_TYPE_PREDEFINED"
                        class="w-100"
                        v-model="bot.default_outbound_campaign_id"
                        data-testid="aloai-agent-outbound-line-select"
                        placeholder="Select default outbound line"
                        filterable
                    >
                        <el-option
                            v-for="campaign in activeCampaigns"
                            :key="campaign.id"
                            :label="campaign.name"
                            :value="campaign.id"
                            :data-testid="`aloai-agent-outbound-line-option-${campaign.id}`"
                        >
                            <el-tooltip
                                :disabled="!isMessagingBlocked(campaign, true)"
                                :content="getMessagingBlocked(campaign)"
                                placement="left"
                            >
                                <span :class="{ 'not-registered': isMessagingBlocked(campaign, true) }">{{ campaign.name }}</span>
                            </el-tooltip>
                        </el-option>
                    </el-select>
                </el-form-item>

                <el-form-item
                    v-if="bot.direction == AloAi.DIRECTION_INBOUND && bot.internal_type !== AloAi.INTERNAL_TYPE_MISSED_CALL_HANDLER"
                    prop="default_inbound_campaign_ids"
                >
                    <div class="form-label">
                        <h5>Inbound lines (Optional)</h5>
                        <small v-if="bot.type === AloAi.TYPE_VOICE">
                            Select the lines that will route incoming calls to this agent. You can also configure this later in Line Settings.
                        </small>
                        <small v-else-if="bot.type === AloAi.TYPE_TEXT">
                            Select the lines that will route incoming text messages to this agent. You can also configure this later in Line Settings.
                        </small>
                    </div>
                    <el-select
                        ref="inboundLinesSelect"
                        class="w-100"
                        v-model="selectedInboundCampaignIds"
                        data-testid="aloai-agent-inbound-line-select"
                        placeholder="Select inbound lines"
                        filterable
                        clearable
                        multiple
                        collapse-tags
                        @change="handleInboundLinesChange"
                    >
                        <el-option
                            v-for="campaign in activeCampaigns"
                            :key="campaign.id"
                            :label="campaign.name"
                            :value="campaign.id"
                            :data-testid="`aloai-agent-inbound-line-option-${campaign.id}`"
                        >
                            <el-tooltip
                                :disabled="!isMessagingBlocked(campaign, true)"
                                :content="getMessagingBlocked(campaign)"
                                placement="left"
                            >
                                <span :class="{ 'not-registered': isMessagingBlocked(campaign, true) }">{{ campaign.name }}</span>
                            </el-tooltip>
                        </el-option>
                    </el-select>
                </el-form-item>

                <el-form-item v-if="bot.direction == AloAi.DIRECTION_OUTBOUND">
                    <div class="form-label">
                        <h5>Allow international engagement</h5>
                        <small>Allow this agent to contact international phone numbers.</small>
                    </div>
                    <el-select
                        class="w-100"
                        v-model="bot.allow_international_outbound"
                        data-testid="aloai-agent-international-calls-select"
                    >
                        <el-option
                            v-for="option in allow_international_outbound_options"
                            :key="option.value"
                            :label="option.name"
                            :value="option.value"
                            :data-testid="`aloai-agent-international-calls-option-${option.value}`"
                        />
                    </el-select>
                </el-form-item>

                <span class="page-anchor" id="ai_models_language_settings" />

                <h4 class="mb-4 _500">AI Models & Language</h4>

                <el-form-item v-if="bot.type === AloAi.TYPE_TEXT">
                    <div class="form-label">
                        <h5>Text model</h5>
                        <small>
                            Choose the text model the agent needs to operate on. Opt for
                            speed or depth to suit your agent's role.
                        </small>
                    </div>
                    <el-select
                        class="w-100"
                        v-model="bot.model"
                        data-testid="aloai-text-model-select"
                        :value-key="'value'"
                    >
                        <el-option
                            v-for="model in modelsWithPricing"
                            :key="model.value"
                            :label="model.name"
                            :value="model.value"
                            :data-testid="`aloai-text-model-option-${model.value}`"
                        >
                            <template #default>
                                <div class="unified-model-option">
                                    <span class="model-name">{{ model.name }}</span>
                                </div>
                            </template>
                        </el-option>
                    </el-select>
                </el-form-item>

                <el-form-item v-if="bot.type === AloAi.TYPE_VOICE && bot.internal_type !== AloAi.INTERNAL_TYPE_MISSED_CALL_HANDLER">
                    <div class="form-label">
                        <h5>
                            AI model for calls
                            <el-popover
                                placement="right"
                                width="450"
                                trigger="hover">
                                <div>
                                    <div class="mb-3">
                                        <span class="d-flex font-weight-bold text-danger">Ultra Premium </span>
                                        <span class="d-block text-muted">High-performance voice-to-voice AI agents. Most natural and human-like conversations. Best for sales or high-end assistants.</span>
                                    </div>
                                    <div class="mb-3">
                                        <span class="d-flex font-weight-bold text-warning">Premium</span>
                                        <span class="d-block text-muted">Efficient voice-to-voice AI Agents offering fast responses with reduced latency and lower cost. Fast, reliable voice AI for everyday customer interactions.</span>
                                    </div>
                                    <div class="mb-0">
                                        <span class="d-flex font-weight-bold text-success">Basic</span>
                                        <span class="d-block text-muted">Affordable voice-to-voice AI Agents. Perfect for assistants, support flows, and task automation.</span>
                                    </div>
                                </div>
                                <i slot="reference"
                                   class="fa fa-info-circle text-primary ml-2">
                                </i>
                            </el-popover>
                        </h5>
                        <small>
                            Choose the AI model the agent needs to operate on when receiving/making calls. Opt for
                            speed or depth to suit your agent's role.
                        </small>
                    </div>
                    <el-select
                        v-model="bot.voice_model"
                        data-testid="aloai-voice-model-select"
                        filterable
                        class="w-100"
                        @change="handleVoiceModelChange"
                        v-if="isVoiceModelsReady"
                    >
                        <el-option-group
                            v-for="group in groupedVoiceModels"
                            :key="group.label"
                            :label="group.label"
                        >
                        <el-option
                            v-for="model in group.models"
                            :key="model.id"
                            :label="getVoiceModelDisplayLabel(model)"
                            :value="model.value"
                            :data-testid="`aloai-voice-model-option-${model.value}`"
                        >
                            <template #default>
                                <div class="unified-model-option">
                                    <span class="model-name">{{ model.name }}</span>
                                    <el-tag
                                        v-if="model.tier"
                                        :type="getTierTagType(model.tier)"
                                        size="mini"
                                        class="tier-badge"
                                    >
                                        {{ model.tier }}
                                    </el-tag>
                                    <span class="model-price" v-if="model.price_per_minute">
                                        ${{ parseFloat(model.price_per_minute).toFixed(2) }}/min
                                    </span>
                                </div>
                            </template>
                        </el-option>
                        </el-option-group>
                    </el-select>
                    <div v-else class="text-muted">Loading voice models...</div>
                </el-form-item>

                <el-form-item v-if="bot.type === AloAi.TYPE_VOICE">
                    <div class="form-label">
                        <h5>Language</h5>
                        <small>Select language for the voice agent to respond in</small>
                    </div>
                    <el-select
                        class="w-100"
                        v-model="bot.language"
                        data-testid="aloai-voice-language-select"
                        filterable
                        placeholder="Select language"
                        @change="handleLanguageChange"
                        v-if="isVoiceDataReady && filteredLanguages && filteredLanguages.length > 0"
                    >
                        <el-option
                            v-for="lang in filteredLanguages"
                            :key="lang.code"
                            :label="lang.name"
                            :value="lang.code"
                            :data-testid="`aloai-voice-language-option-${lang.code}`"
                        />
                    </el-select>
                    <div v-else class="text-muted">Loading languages...</div>
                </el-form-item>

                <el-form-item v-if="bot.type === AloAi.TYPE_VOICE">
                    <div class="form-label">
                        <h5>Select voice</h5>
                        <small> Choose a preset voice for this agent. </small>
                    </div>
                    <div class="d-flex align-items-center voice-selector-width">
                        <el-select
                            class="w-100"
                            v-model="bot.voice_id"
                            data-testid="aloai-voice-preset-select"
                            filterable
                            placeholder="Select voice"
                            @change="handleVoiceChange"
                            v-if="isVoiceDataReady && groupedVoices && groupedVoices.length > 0"
                        >
                            <el-option-group
                                v-for="group in groupedVoices"
                                :key="group.label"
                                :label="group.label"
                            >
                                <el-option
                                    v-for="voice in group.voices"
                                    :key="voice.id"
                                    :label="getFormattedVoiceLabel(voice)"
                                    :value="voice.id"
                                    :data-testid="`aloai-voice-preset-option-${voice.id}`"
                                >
                                    <template #default>
                                        <div class="voice-option">
                                            <span class="gender-circle"
                                                  :class="getGenderClass(voice.gender)">
                                                {{ getGenderName(voice.gender) }}
                                            </span>
                                            {{ getVoiceName(voice) }}
                                        </div>
                                        <div class="voice-tags mb-1 w-75">
                                            <el-tag
                                                v-if="getLanguage(voice.language)"
                                                size="mini"
                                                class="mr-1 voice-tag"
                                                type="info"
                                            >
                                                {{ getLanguage(voice.language) }}

                                                <span
                                                    v-if="getAccent(voice.accent)"
                                                    class="accent-tag"
                                                >
                                                    ({{ getAccent(voice.accent) }})
                                                </span>
                                            </el-tag>
                                        </div>
                                    </template>
                                </el-option>
                            </el-option-group>
                        </el-select>
                        <div v-else class="text-muted">Loading voices...</div>
                        <el-button
                            v-if="bot.voice_id"
                            class="ml-2"
                            circle
                            data-testid="aloai-voice-play-btn"
                            @click="toggleAudio(bot.voice_id)"
                        >
                            <i :class="isPlaying ? 'fas fa-stop' : 'fas fa-play'"></i>
                        </el-button>
                    </div>
                </el-form-item>

                <!-- Call Settings for Outbound Voice Bots -->
                <div v-if="bot.type === AloAi.TYPE_VOICE && bot.direction === AloAi.DIRECTION_OUTBOUND">
                    <span class="page-anchor" id="call_settings" />
                    <h4 class="mb-4 _500">Call Settings</h4>
                    <el-form-item>
                        <div class="form-label">
                            <h5>Voicemail Detection</h5>
                            <small>
                                Automatically detect when a call reaches voicemail and configure the agent's behavior.
                            </small>
                        </div>
                        <el-switch
                            v-model="bot.voicemail_detection_enabled"
                            data-testid="aloai-voicemail-detection-switch"
                        />
                    </el-form-item>

                    <div v-if="bot.voicemail_detection_enabled">
                        <el-form-item>
                            <div class="form-label">
                                <h5>Voicemail Behavior</h5>
                                <small>Choose what the agent should do when voicemail is detected.</small>
                            </div>
                            <el-radio-group
                                v-model="bot.voicemail_behavior"
                                class="w-100"
                                data-testid="aloai-voicemail-behavior-radio-group"
                                @change="onVoicemailBehaviorChange">
                                <el-radio
                                    label="hang_up"
                                    class="w-100"
                                    data-testid="aloai-voicemail-behavior-radio-hang-up">
                                    <strong>Hang up</strong> - Disconnect immediately when voicemail is detected
                                </el-radio>
                                <el-radio
                                    label="leave_message"
                                    class="w-100"
                                    data-testid="aloai-voicemail-behavior-radio-leave-message">
                                    <strong>Leave a message</strong> - Wait for the beep and deliver a configured message
                                </el-radio>
                            </el-radio-group>
                        </el-form-item>

                        <div v-if="bot.voicemail_behavior === 'leave_message'">
                            <el-form-item>
                                <div class="form-label">
                                    <h5>Message Type</h5>
                                    <small>Choose between a static message or dynamic AI-generated content.</small>
                                </div>
                                <el-radio-group
                                    v-model="bot.voicemail_use_dynamic"
                                    class="w-100"
                                    data-testid="aloai-voicemail-message-type-radio-group"
                                    @change="onVoicemailMessageTypeChange">
                                    <el-radio
                                        :label="false"
                                        class="w-100"
                                        data-testid="aloai-voicemail-message-type-radio-static">
                                        <strong>Static Message</strong> - Use a pre-written message
                                    </el-radio>
                                    <el-radio
                                        :label="true"
                                        class="w-100"
                                        data-testid="aloai-voicemail-message-type-radio-dynamic">
                                        <strong>Dynamic Prompt</strong> - AI generates message based on context
                                    </el-radio>
                                </el-radio-group>
                            </el-form-item>

                            <el-form-item v-if="!bot.voicemail_use_dynamic">
                                <div class="form-label">
                                    <h5>Static Voicemail Message</h5>
                                    <small>
                                        Enter the message to leave on voicemail. Use the <strong>Add variable</strong> button to insert contact information and other dynamic content.
                                        <br><br>
                                        <strong>Examples:</strong> "Hello, I was calling about your recent inquiry. Please call me back at your convenience." or "Hi, I have important information to share with you. Please return my call."
                                    </small>
                                </div>
                                <el-input
                                    type="textarea"
                                    :rows="3"
                                    ref="voicemailMessageInput"
                                    v-model="bot.voicemail_message"
                                    data-testid="aloai-voicemail-message-input"
                                    placeholder=""
                                    maxlength="1000"
                                    show-word-limit
                                    @focus="onVoicemailMessageFocus"
                                    @blur="onVoicemailMessageBlur"
                                />
                                <div 
                                    class="d-flex align-items-center mt-2" 
                                    v-if="bot.allow_access_contact_information"
                                    data-testid="aloai-voicemail-message-variables-container"
                                >
                                    <el-tooltip content="Add variable" placement="top">
                                        <variables 
                                            data-testid="aloai-voicemail-message-variables"
                                            @variable-selected="variableSelectedForVoicemailMessage" 
                                        />
                                    </el-tooltip>
                                </div>
                            </el-form-item>

                            <el-form-item v-if="bot.voicemail_use_dynamic">
                                <div class="form-label">
                                    <h5>Dynamic Voicemail Prompt</h5>
                                    <small>
                                        Provide instructions for the AI to generate a personalized voicemail message based on the call context. The AI will create a unique message for each voicemail using the conversation details and contact information.
                                        <br><br>
                                        <strong>Examples:</strong> "Summarize why I called and ask them to call me back" or "Create a friendly follow-up message mentioning what we discussed and provide next steps."
                                    </small>
                                </div>
                                <el-input
                                    type="textarea"
                                    :rows="3"
                                    ref="voicemailPromptInput"
                                    v-model="bot.voicemail_prompt"
                                    data-testid="aloai-voicemail-prompt-input"
                                    placeholder=""
                                    maxlength="1000"
                                    show-word-limit
                                    @focus="onVoicemailPromptFocus"
                                    @blur="onVoicemailPromptBlur"
                                />
                                <div 
                                    class="d-flex align-items-center mt-2" 
                                    v-if="bot.allow_access_contact_information"
                                    data-testid="aloai-voicemail-prompt-variables-container"
                                >
                                    <el-tooltip content="Add variable" placement="top">
                                        <variables 
                                            data-testid="aloai-voicemail-prompt-variables"
                                            @variable-selected="variableSelectedForVoicemailPrompt" 
                                        />
                                    </el-tooltip>
                                </div>
                            </el-form-item>
                        </div>
                    </div>
                </div>

                <div v-if="bot.type !== AloAi.TYPE_VOICE">
                    <span class="page-anchor" id="engagement_settings" />

                    <h4 class="mb-4 _500">Engagement</h4>

                    <el-form-item>
                        <div class="form-label">
                            <h5>Engagement expiration</h5>
                            <small>
                                How long will the agent's engagement with a contact last before it expires?
                            </small>
                        </div>
                        <div class="row align-items-center">
                            <div class="col-6">
                                <el-form-item
                                    class="mb-0"
                                    prop="ttl"
                                >
                                    <el-input-number
                                        class="w-100"
                                        v-model="bot_ttl"
                                        data-testid="aloai-agent-engagement-expiration-input"
                                        :min="1"
                                        :max="14"
                                        :precision="0"
                                        :controls="true"
                                        @change="updateBotTTL"
                                    />
                                </el-form-item>
                            </div>
                            <div class="col-6">
                                Days
                            </div>
                        </div>
                    </el-form-item>
                </div>
            </div>

            <div class="col-md-3 d-none d-md-block">
                <on-this-page
                    :items="getOnThisPageItems()"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import * as AloAi from '../../../../constants/aloai'
import * as TagCategory from '../../../../constants/tag-categories'
import { acl_mixin, aloai_mixin, campaign_mixin, contact_phone_number_mixin } from '../../../../mixins'
import audioPlayerMixin from '../../../../mixins/audio-player-mixin'
import voiceUtilsMixin from '../../../../mixins/voice-utils-mixin'
import OnThisPage from '../../../common/on-this-page.vue'
import Variables from '../../../messenger/variables.vue'
import TagSelector from '../../../tag-selector.vue'

export default {
    name: 'configure-tab',

    components: {
        OnThisPage,
        TagSelector,
        Variables
    },

    mixins: [acl_mixin, aloai_mixin, campaign_mixin, contact_phone_number_mixin, voiceUtilsMixin, audioPlayerMixin],

    props: {
        bot: {
            type: Object,
            required: true
        },
        campaigns: {
            type: Array,
            default: () => []
        },
        botId: {
            type: [String, Number],
            default: null
        },
        defaultOutboundType: {
            type: String,
            default: () => AloAi.OUTBOUND_TYPE_LAST_LINE
        }
    },

    data() {
        return {
            AloAi,
            TagCategory,
            pendingInboundCampaignIds: [],
            selectedInboundCampaignIds: [],
            updatingInboundLines: false,
            access_contact_information_options: [
                {
                    name: 'Yes',
                    value: true
                }, {
                    name: 'No',
                    value: false
                }
            ],
            allow_international_outbound_options: [
                {
                    name: 'Yes',
                    value: true
                }, {
                    name: 'No',
                    value: false
                }
            ],
            outbound_types: [{
                name: 'Select the last line used by agent',
                value: AloAi.OUTBOUND_TYPE_LAST_LINE
            }, {
                name: 'Select the default outbound line for this agent',
                value: AloAi.OUTBOUND_TYPE_PREDEFINED
            }],
            types: [{
                name: 'Text',
                value: AloAi.TYPE_TEXT
            }, {
                name: 'Voice',
                value: AloAi.TYPE_VOICE
            }],
            directions: [{
                name: 'Inbound',
                value: AloAi.DIRECTION_INBOUND
            }, {
                name: 'Outbound',
                value: AloAi.DIRECTION_OUTBOUND
            }],
            models: [{
                name: 'GPT-5.4',
                value: AloAi.MODEL_GPT_54
            }, {
                name: 'GPT-5.4 Mini',
                value: AloAi.MODEL_GPT_54_MINI
            }, {
                name: 'GPT-5.4 Nano',
                value: AloAi.MODEL_GPT_54_NANO
            }, {
                name: 'GPT-5.2',
                value: AloAi.MODEL_GPT_52
            }, {
                name: 'GPT-5.1',
                value: AloAi.MODEL_GPT_51
            }, {
                name: 'GPT-5',
                value: AloAi.MODEL_GPT_5
            }, {
                name: 'GPT-5 Mini',
                value: AloAi.MODEL_GPT_5_MINI
            }, {
                name: 'GPT-5 Nano',
                value: AloAi.MODEL_GPT_5_NANO
            }, {
                name: 'GPT-4.1',
                value: AloAi.MODEL_GPT_41
            }, {
                name: 'GPT-4.1 Mini',
                value: AloAi.MODEL_GPT_41_MINI
            }, {
                name: 'GPT-4.1 Nano',
                value: AloAi.MODEL_GPT_41_NANO
            }, {
                name: 'GPT-4o',
                value: AloAi.MODEL_GPT_4O
            }, {
                name: 'GPT-4o Mini',
                value: AloAi.MODEL_GPT_4O_MINI
            }],
            bot_ttl: 7,
            // Audio related data
            voiceLoading: false,
            voiceModelLoading: false,
            languageLoading: false,
            // Cursor position tracking for variable insertion
            voicemailMessageCursorPosition: 0,
            voicemailPromptCursorPosition: 0
        }
    },

    computed: {
        ...mapState(['users', 'ring_groups', 'ivrs']),
        ...mapState('aloai', ['voices', 'voiceModels', 'languages']),
        ...mapGetters('aloai', [
            'getVoicesLoading',
            'getVoiceModelsLoading',
            'getLanguagesLoading',
            'getFilteredLanguages',
            'getFilteredVoices',
            'getGroupedVoices',
            'getGroupedVoiceModels',
            'getDefaultVoiceForModel'
        ]),

        isLoading() {
            return this.getVoiceModelsLoading || this.getLanguagesLoading || this.voiceLoading || this.voiceModelLoading || this.languageLoading
        },

        blockAgentTypeChange() {
            return this.botId ? true : false
        },



        filteredLanguages() {
            return this.getFilteredLanguages(this.bot.voice_id) || []
        },

        filteredVoices() {
            return this.getFilteredVoices(this.bot.voice_model) || []
        },

        activeCampaigns() {
            return this.campaigns.filter(campaign => campaign.active === true)
        },

        groupedVoices() {
            return this.getGroupedVoices(this.bot.voice_model) || []
        },

        groupedVoiceModels() {
            return this.getGroupedVoiceModels || []
        },

        voices_list() {
            return this.voices || []
        },

        voice_models() {
            return this.voiceModels || []
        },

        // Check if voice data is ready
        isVoiceDataReady() {
            return this.voices && this.voices.length > 0 &&
                   this.voiceModels && this.voiceModels.length > 0 &&
                   this.languages && this.languages.length > 0
        },

        isIvaBot() {
            return this.bot.internal_type === AloAi.INTERNAL_TYPE_IVA
        },

        // Check if voice models are ready for display
        isVoiceModelsReady() {
            return this.isVoiceDataReady && this.groupedVoiceModels && this.groupedVoiceModels.length > 0
        },

        // Get text models with pricing information
        modelsWithPricing() {
            return this.models.map(model => {
                // Get pricing for this text model
                const price = this.getTextModelPrice(model.value)
                return {
                    ...model,
                    price_per_enrollment: price
                }
            })
        },

        // Determine if the Inbound Lines section should be displayed
        shouldShowInboundLinesSection() {
            return this.botId && 
                   this.bot.direction === AloAi.DIRECTION_INBOUND &&
                   this.bot.internal_type !== AloAi.INTERNAL_TYPE_MISSED_CALL_HANDLER
        },

        // Get the list of inbound lines connected to this bot
        connectedInboundLines() {
            if (!this.bot.campaigns) return []
            const numericBotId = parseInt(this.botId, 10)
            return this.bot.type === AloAi.TYPE_VOICE
                ? this.bot.campaigns.filter(c => c.aloai_bot_id === numericBotId)
                : this.bot.campaigns.filter(c => c.aloai_text_bot_id === numericBotId)
        },

    },

    watch: {
        // Watch for voice data changes to ensure bot voice selections are properly set
        isVoiceDataReady(newVal) {
            if (newVal && this.bot.voice_id && this.bot.voice_model && this.bot.language) {
                // Force a re-render when voice data becomes available
                this.$nextTick(() => {
                    this.$forceUpdate()
                })
            }
        },

        // Watch for voicemail detection being enabled to set default behavior
        'bot.voicemail_detection_enabled'(newVal) {
            if (newVal && !this.bot.voicemail_behavior) {
                // Set default behavior to "hang_up" when voicemail detection is enabled
                this.bot.voicemail_behavior = 'hang_up'
            }
        },

        // Watch for bot campaigns being loaded to sync inbound campaigns
        'bot.campaigns': {
            handler(newVal) {
                // Only for inbound bots
                if (this.bot.direction !== AloAi.DIRECTION_INBOUND || !newVal) {
                    return
                }
                
                // Don't interfere if user is actively changing lines
                if (this.updatingInboundLines) {
                    return
                }
                
                // Sync the inbound campaigns
                this.syncInboundCampaigns()
            },
            immediate: true,
            deep: true
        },

        // Watch for voicemail behavior changes to set default message type
        'bot.voicemail_behavior'(newVal) {
            if (newVal === 'leave_message' && (this.bot.voicemail_use_dynamic === null || this.bot.voicemail_use_dynamic === undefined)) {
                // Set default to static message (false) when "leave_message" is selected
                this.bot.voicemail_use_dynamic = false
            }
        }
    },

    mounted() {
        if (this.bot.ttl) {
            this.bot_ttl = this.bot.ttl / 60 / 24
        }

        // Sync inbound campaigns from bot data
        this.syncInboundCampaigns()

        // Voice data is now loaded by the parent dialog component
        // Set default voice if none selected and voices are available
        this.$nextTick(() => {
            if (!this.bot.voice_id && this.voices && this.voices.length > 0) {
                // Use the default voice getter to select Adrian as default
                const defaultVoice = this.getDefaultVoiceForModel(this.bot.voice_model)
                this.bot.voice_id = defaultVoice ? defaultVoice.id : this.voices[0].id
            }

            // Set voicemail defaults if voicemail detection is enabled but no behavior is set
            if (this.bot.voicemail_detection_enabled && !this.bot.voicemail_behavior) {
                this.bot.voicemail_behavior = 'hang_up'
            }

            // Set default message type if leave_message is selected but no type is set
            if (this.bot.voicemail_behavior === 'leave_message' && (this.bot.voicemail_use_dynamic === null || this.bot.voicemail_use_dynamic === undefined)) {
                this.bot.voicemail_use_dynamic = false
            }
        })
    },

    methods: {
        ...mapActions('aloai', ['fetchVoices', 'fetchVoiceModels', 'fetchLanguages', 'fetchAllVoiceData']),
        ...mapActions(['updateCampaign']),

        syncInboundCampaigns() {
            // Only for inbound bots
            if (this.bot.direction !== AloAi.DIRECTION_INBOUND || !this.bot.campaigns) {
                this.selectedInboundCampaignIds = []
                return
            }

            // Convert botId to number for consistent comparison (API returns integers)
            const numericBotId = this.botId ? parseInt(this.botId, 10) : null

            // Find all campaigns that are connected to this bot
            const connectedCampaigns = this.bot.type === AloAi.TYPE_VOICE
                ? this.bot.campaigns.filter(c => c.aloai_bot_id === numericBotId)
                : this.bot.campaigns.filter(c => c.aloai_text_bot_id === numericBotId)

            // Extract the IDs of connected campaigns
            this.selectedInboundCampaignIds = connectedCampaigns.map(c => c.id)
        },

        // Get the phone number for a line (campaign)
        getLinePhoneNumber(line) {
            if (line.incoming_numbers && line.incoming_numbers.length > 0) {
                return line.incoming_numbers[0]?.phone_number
            }
            return null
        },

        // Scroll to the inbound lines selector dropdown
        scrollToInboundLinesSelector() {
            const selector = document.querySelector('[data-testid="aloai-agent-inbound-line-select"]')
            if (selector) {
                selector.scrollIntoView({ behavior: 'smooth', block: 'center' })
                // Focus the selector after scrolling
                this.$nextTick(() => {
                    const input = selector.querySelector('input')
                    if (input) {
                        input.focus()
                        input.click()
                    }
                })
            }
        },

        updateBotTTL() {
            this.bot.ttl = this.bot_ttl * 60 * 24
        },

        // Radio button change handlers to ensure proper reactivity
        onVoicemailBehaviorChange(value) {
            this.bot.voicemail_behavior = value

            // Set default message type when switching to leave_message
            if (value === 'leave_message' && (this.bot.voicemail_use_dynamic === null || this.bot.voicemail_use_dynamic === undefined)) {
                this.bot.voicemail_use_dynamic = false
            }
        },

        onVoicemailMessageTypeChange(value) {
            // Explicitly set the value to ensure reactivity
            this.bot.voicemail_use_dynamic = value
        },


        // Variable insertion methods for voicemail inputs
        onVoicemailMessageFocus(event) {
            this.voicemailMessageCursorPosition = event.target.selectionStart
        },

        onVoicemailMessageBlur(event) {
            this.voicemailMessageCursorPosition = event.target.selectionStart
        },

        onVoicemailPromptFocus(event) {
            this.voicemailPromptCursorPosition = event.target.selectionStart
        },

        onVoicemailPromptBlur(event) {
            this.voicemailPromptCursorPosition = event.target.selectionStart
        },

        variableSelectedForVoicemailMessage(variable) {
            let content = this.bot.voicemail_message || ''
            content = content.slice(0, this.voicemailMessageCursorPosition) + variable + content.slice(this.voicemailMessageCursorPosition)
            this.voicemailMessageCursorPosition = this.voicemailMessageCursorPosition + variable.length
            this.bot.voicemail_message = content

            // Focus back on the message input
            this.$nextTick(() => {
                const input = this.$refs.voicemailMessageInput.$el.querySelector('textarea')
                if (input) {
                    input.focus()
                    input.setSelectionRange(this.voicemailMessageCursorPosition, this.voicemailMessageCursorPosition)
                }
            })
        },

        variableSelectedForVoicemailPrompt(variable) {
            let content = this.bot.voicemail_prompt || ''
            content = content.slice(0, this.voicemailPromptCursorPosition) + variable + content.slice(this.voicemailPromptCursorPosition)
            this.voicemailPromptCursorPosition = this.voicemailPromptCursorPosition + variable.length
            this.bot.voicemail_prompt = content

            // Focus back on the prompt input
            this.$nextTick(() => {
                const input = this.$refs.voicemailPromptInput.$el.querySelector('textarea')
                if (input) {
                    input.focus()
                    input.setSelectionRange(this.voicemailPromptCursorPosition, this.voicemailPromptCursorPosition)
                }
            })
        },

        handleBotTypeChange() {
            if (this.bot.type === AloAi.TYPE_VOICE) {
                this.bot.direction = AloAi.DIRECTION_INBOUND
                this.bot.follow_up_enabled = false
            }
            this.bot.allow_international_outbound = false
        },

        handleBotDirectionChange() {
            this.bot.allow_international_outbound = false
        },

        async handleInboundLinesChange(newCampaignIds) {
            // If bot hasn't been created yet
            if (!this.botId) {
                const selectedIds = newCampaignIds || []
                
                // Check for conflicts with selected lines
                if (selectedIds.length > 0) {
                    const hasConflict = await this.checkLineConflicts(selectedIds)
                    if (!hasConflict) {
                        // User cancelled, revert selection
                        this.selectedInboundCampaignIds = [...this.pendingInboundCampaignIds]
                        return
                    }
                }
                
                // Store the values and emit to parent
                this.pendingInboundCampaignIds = selectedIds
                this.$emit('pending-inbound-lines-change', selectedIds)
                return
            }

            // Calculate which lines to add and which to remove
            const numericBotId = parseInt(this.botId, 10)
            const currentlyConnectedIds = this.bot.type === AloAi.TYPE_VOICE
                ? this.bot.campaigns?.filter(c => c.aloai_bot_id === numericBotId).map(c => c.id) || []
                : this.bot.campaigns?.filter(c => c.aloai_text_bot_id === numericBotId).map(c => c.id) || []

            const linesToAdd = newCampaignIds.filter(id => !currentlyConnectedIds.includes(id))
            const linesToRemove = currentlyConnectedIds.filter(id => !newCampaignIds.includes(id))

            // If no changes, do nothing
            if (linesToAdd.length === 0 && linesToRemove.length === 0) {
                return
            }

            // Check for conflicts with lines being added
            if (linesToAdd.length > 0) {
                const hasConflict = await this.checkLineConflicts(linesToAdd)
                if (!hasConflict) {
                    this.syncInboundCampaigns()
                    return
                }
            }

            // Update the lines
            await this.updateInboundLines(linesToAdd, linesToRemove)
        },

        async checkLineConflicts(linesToAdd) {
            // Get campaigns that are being added
            const campaignsToCheck = this.activeCampaigns.filter(c => linesToAdd.includes(c.id))

            const conflictingLines = []

            for (const campaign of campaignsToCheck) {
                const conflicts = []

                // Check routing priority (same as settings.vue logic)
                // Priority: User > Ring Group > IVR > AloAi > Dead End
                if (campaign.user_id) {
                    const user = this.users?.find(u => u.id === campaign.user_id)
                    if (user) {
                        conflicts.push(`User: ${user.name}`)
                    } else {
                        conflicts.push('A specific user')
                    }
                } else if (campaign.ring_group_id) {
                    const ringGroup = this.ring_groups?.find(rg => rg.id === campaign.ring_group_id)
                    if (ringGroup) {
                        conflicts.push(`Inbox: ${ringGroup.name}`)
                    } else {
                        conflicts.push('An Inbox')
                    }
                } else if (campaign.ivr_id) {
                    const ivr = this.ivrs?.find(i => i.id === campaign.ivr_id)
                    if (ivr) {
                        conflicts.push(`IVR: ${ivr.name}`)
                    } else {
                        conflicts.push('An IVR')
                    }
                } else if (campaign.aloai_bot_id && this.bot.type === AloAi.TYPE_VOICE) {
                    conflicts.push('Another AloAi Voice Agent')
                } else if (campaign.aloai_text_bot_id && this.bot.type === AloAi.TYPE_TEXT) {
                    conflicts.push('Another AloAi Text Agent')
                }

                if (conflicts.length > 0) {
                    conflictingLines.push({
                        name: campaign.name,
                        conflicts: conflicts
                    })
                }
            }
            
            // If no conflicts, proceed
            if (conflictingLines.length === 0) {
                return true
            }
            
            // Build confirmation message
            let message = '<div style="text-align: left;">'
            message += '<p><strong>The following line(s) are currently connected to other destinations:</strong></p>'
            message += '<ul style="margin: 10px 0;">'
            
            for (const line of conflictingLines) {
                message += `<li><strong>${line.name}</strong><br/>`
                message += `<span style="color: #E6A23C; font-size: 13px;">Currently connected to: ${line.conflicts.join(', ')}</span></li>`
            }
            
            message += '</ul>'
            message += '<p style="margin-top: 15px; color: #606266;">Connecting these lines to this agent will disconnect them from their current destinations. Do you want to continue?</p>'
            message += '</div>'

            await this.$nextTick()
            document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window, button: 0 }))
            document.body.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window, button: 0 }))
            await new Promise(resolve => setTimeout(resolve, 500))

            try {
                await this.$confirm(message, 'Line Connection Conflict', {
                    confirmButtonText: 'Yes, Connect Lines',
                    cancelButtonText: 'Cancel',
                    dangerouslyUseHTMLString: true,
                    type: 'warning',
                    customClass: 'line-conflict-modal'
                })
                return true
            } catch {
                return false
            }
        },

        async updateInboundLines(linesToAdd, linesToRemove) {
            this.updatingInboundLines = true

            try {
                const response = await axios.put(`/api/v1/aloai/bot/${this.botId}/update-inbound-lines`, {
                    add_campaign_ids: linesToAdd,
                    remove_campaign_ids: linesToRemove
                })

                // Update local bot data with response
                if (response.data && response.data.campaigns) {
                    this.$set(this.bot, 'campaigns', response.data.campaigns)
                }

                // Sync the selected IDs from the updated bot data
                this.syncInboundCampaigns()

                // Update Vuex store for all affected campaigns
                const allAffectedCampaignIds = [...linesToAdd, ...linesToRemove]
                for (const campaignId of allAffectedCampaignIds) {
                    try {
                        const campaignResponse = await axios.get(`/api/v1/campaign/${campaignId}`)
                        this.updateCampaign(campaignResponse.data)
                    } catch (error) {
                        console.warn('Failed to update campaign in store:', campaignId, error)
                    }
                }

                const addedCount = linesToAdd.length
                const removedCount = linesToRemove.length
                let message = ''
                
                if (addedCount > 0 && removedCount > 0) {
                    message = `${addedCount} line(s) connected and ${removedCount} line(s) disconnected.`
                } else if (addedCount > 0) {
                    message = `${addedCount} line(s) successfully connected to this agent.`
                } else if (removedCount > 0) {
                    message = `${removedCount} line(s) successfully disconnected from this agent.`
                }

                if (message) {
                    this.$notify.success({
                        offset: 95,
                        title: 'Lines Updated',
                        message: message,
                        duration: 3000
                    })
                }

                // Emit event to parent to refresh campaign list
                this.$emit('lines-updated', { added: linesToAdd, removed: linesToRemove })
            } catch (error) {
                this.$notify.error({
                    offset: 95,
                    title: 'Update Failed',
                    message: error.response?.data?.message || 'Failed to update inbound lines',
                    duration: 5000
                })
                // Revert selection on error
                this.syncInboundCampaigns()
            } finally {
                this.updatingInboundLines = false
            }
        },

        getMessagingBlocked(campaign) {
            if (campaign?.blocked_messaging_information?.reason) {
                return campaign.blocked_messaging_information.reason
            }

            if (!this.shouldAllowSmsTraffic(campaign)) {
                return 'To send messages to the US, A2P 10DLC Brand and Campaign are required.'
            }

            return 'Messaging is currently disabled.'
        },

        handleVoiceChange(voiceId) {
            this.stopAudio()

            // Store the previous voice ID for potential rollback
            const previousVoiceId = this.bot.voice_id

            // Check if current model is S2S and if selected voice is compatible
            const isS2sModel = this.bot.voice_model === AloAi.VOICE_MODEL_GPT_REALTIME_15 ||
                               this.bot.voice_model === AloAi.VOICE_MODEL_GPT_REALTIME_MINI

            if (isS2sModel) {
                // Get selected voice
                const selectedVoice = this.voices_list.find(voice => voice.id === voiceId)

                // Check if voice is compatible (must be OpenAI and not in unsupported list)
                const isVoiceCompatible = selectedVoice &&
                                          selectedVoice.provider === AloAi.PROVIDER_OPENAI &&
                                          !AloAi.UNSUPPORTED_S2S_VOICES.includes(selectedVoice.external_id)

                // If not compatible, show error and reset to previous voice
                if (!isVoiceCompatible) {
                    this.$notify.error({
                        offset: 95,
                        title: 'Voice Selection Error',
                        message: 'The selected voice is not compatible with the current voice model. Please choose an OpenAI compatible voice.',
                        duration: 5000
                    })

                    // Reset voice selection to previous voice
                    this.bot.voice_id = previousVoiceId
                    return
                }
            }

            this.bot.voice_id = voiceId

            if (!this.botId) {
                return // Skip API call if the bot hasn't been created yet
            }

            // Make API call to update the voice
            this.voiceLoading = true
            axios.put(`/api/v1/aloai/bot/${this.botId}/update-voice`, {
                voice_id: voiceId
            })
            .then(() => {
                this.$notify.success({
                    offset: 95,
                    title: 'Voice Updated',
                    message: 'The bot voice has been updated successfully.',
                    duration: 3000
                })
            })
            .catch(error => {
                this.$notify.error({
                    offset: 95,
                    title: 'Update Failed',
                    message: error.response?.data?.message || 'Failed to update the voice',
                    duration: 5000
                })
            })
            .finally(() => {
                this.voiceLoading = false
            })
        },

        handleLanguageChange(code) {
            this.bot.language = code

            if (!this.botId) {
                return // Skip API call if the bot hasn't been created yet
            }

            // Make API call to update the language
            this.languageLoading = true
            axios.put(`/api/v1/aloai/bot/${this.botId}/update-language`, {
                language: code
            })
            .then(() => {
                this.$notify.success({
                    offset: 95,
                    title: 'Language Updated',
                    message: 'The bot language has been updated successfully.',
                    duration: 3000
                })
            })
            .catch(error => {
                this.$notify.error({
                    offset: 95,
                    title: 'Update Failed',
                    message: error.response?.data?.message || 'Failed to update the language',
                    duration: 5000
                })
            })
            .finally(() => {
                this.languageLoading = false
            })
        },

        handleVoiceModelChange(modelValue) {
            // Check if changing to S2S model and if current voice is compatible
            const isChangingToS2sModel = modelValue === AloAi.VOICE_MODEL_GPT_REALTIME_15 ||
                                         modelValue === AloAi.VOICE_MODEL_GPT_REALTIME_MINI

            if (isChangingToS2sModel) {
                // Get current voice
                const currentVoice = this.voices_list.find(voice => voice.id === this.bot.voice_id)

                // Check if voice is OpenAI and not in unsupported list
                const isVoiceCompatible = currentVoice &&
                                          currentVoice.provider === AloAi.PROVIDER_OPENAI &&
                                          !AloAi.UNSUPPORTED_S2S_VOICES.includes(currentVoice.external_id)

                // If not compatible, find a default compatible voice
                if (!isVoiceCompatible) {
                    const defaultVoice = this.voices_list.find(voice =>
                        voice.provider === AloAi.PROVIDER_OPENAI &&
                        voice.external_id === AloAi.DEFAULT_OPENAI_VOICE_ID
                    )

                    if (defaultVoice) {
                        this.bot.voice_id = defaultVoice.id
                    }
                }
            }

            this.bot.voice_model = modelValue

            if (!this.botId) {
                return // Skip API call if the bot hasn't been created yet
            }

            // Make API call to update the voice model
            this.voiceModelLoading = true
            axios.put(`/api/v1/aloai/bot/${this.botId}/update-voice-model`, {
                voice_model: modelValue
            })
            .then(() => {
                this.$notify.success({
                    offset: 95,
                    title: 'Voice Model Updated',
                    message: 'The bot voice model has been updated successfully.',
                    duration: 3000
                })
            })
            .catch(error => {
                this.$notify.error({
                    offset: 95,
                    title: 'Update Failed',
                    message: error.response?.data?.message || 'Failed to update the voice model',
                    duration: 5000
                })
            })
            .finally(() => {
                this.voiceModelLoading = false
            })
        },

        getTextModelPrice(modelValue) {
            const company = this.$store.getters['cache/getCurrentCompany']
            const rate = company?.rate

            return rate?.aloai_text_bot_enrollment || 0.10
        },

        getVoiceModelDisplayLabel(model) {
            const tier = model.tier ? model.tier : ''
            const price = model.price_per_minute ? `$${parseFloat(model.price_per_minute).toFixed(2)}/min` : ''
            return `${model.name} | ${tier} | ${price}`.trim()
        },

        getOnThisPageItems() {
            const items = []

            // Add Inbound Lines for existing inbound bots (at the top)
            if (this.shouldShowInboundLinesSection) {
                items.push({ id: 'inbound_lines', label: 'Inbound Lines' })
            }

            // Always add Basic Information
            items.push({ id: 'basic_information', label: 'Basic Information' })

            // Add Communication Settings for non-Call Rescue bots
            if (this.bot.internal_type !== AloAi.INTERNAL_TYPE_MISSED_CALL_HANDLER) {
                items.push({ id: 'communication_settings', label: 'Communication Settings' })
            }

            // Always add AI Models & Language
            items.push({ id: 'ai_models_language_settings', label: 'AI Models & Language' })

            // Add Call Settings only for voice bots with outbound direction
            if (this.bot.type === AloAi.TYPE_VOICE && this.bot.direction === AloAi.DIRECTION_OUTBOUND) {
                items.push({ id: 'call_settings', label: 'Call Settings' })
            }

            // Add Engagement only for non-voice bots (simplified logic)
            if (this.bot.type !== AloAi.TYPE_VOICE) {
                items.push({ id: 'engagement_settings', label: 'Engagement' })
            }

            return items
        }
    }
}
</script>

<style scoped>
.description-text {
    padding: 8px 12px;
    background-color: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    color: #606266;
    font-size: 14px;
    line-height: 1.5;
}
</style>

<style scoped>
.voice-selector-width {
    width: 350px;
}

.voice-option {
    display: flex;
    align-items: center;
}

.gender-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: 5px;
    color: white;
    font-size: 10px;
}

.gender-male {
    background-color: rgb(86, 175, 248);
}

.gender-female {
    background-color: rgb(250, 202, 209);
    border: 1px solid pink;
    color: white;
}

.gender-unknown {
    background-color: grey;
}

.voice-tags {
    display: flex;
    flex-wrap: wrap;
}

.voice-tag {
    color: black;
}

.accent-tag {
    margin-left: 1px;
}

.not-registered::after {
    content: "i";
    position: absolute;
    right: 40px;
    display: inline-block;
    width: 30px;
    height: 25px;
    line-height: 25px;
    border-radius: 10px;
    text-align: center;
    top: 0;
    bottom: 0;
    margin: auto 0;
    background: #0071e2;
    color: #fff;
    z-index: 1;
}

.unified-model-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    flex-wrap: wrap;
}

.model-name {
    font-weight: 500;
    color: #303133;
    flex-shrink: 0;
}

.tier-badge {
    font-weight: 500;
    flex-shrink: 0;
}

.model-price {
    font-size: 12px;
    color: #909399;
    font-weight: 500;
    flex-shrink: 0;
    margin-left: auto;
}

/* Inbound Lines Section Styles */
.inbound-lines-section {
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%);
    border: 1px solid rgba(37, 99, 235, 0.2);
    border-radius: 8px;
    padding: 20px;
    transition: all 0.3s ease;
}

.inbound-lines-section:hover {
    border-color: rgba(37, 99, 235, 0.35);
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
}

.lines-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.line-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e4e7ed;
    transition: all 0.2s ease;
}

.line-item:hover {
    border-color: #409EFF;
    box-shadow: 0 2px 6px rgba(64, 158, 255, 0.15);
}

.line-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.line-name {
    font-weight: 500;
    color: #303133;
    font-size: 14px;
}

.line-number {
    font-size: 13px;
    color: #909399;
    margin-left: 22px;
}

.go-to-line-btn {
    flex-shrink: 0;
    border-color: #409EFF;
    color: #409EFF;
}

.go-to-line-btn:hover {
    background-color: #409EFF;
    color: white;
}

.empty-state {
    padding: 16px 20px;
    text-align: center;
    color: #909399;
    background: #f5f7fa;
    border-radius: 6px;
    border: 1px dashed #dcdfe6;
    font-size: 14px;
}

.empty-state i {
    color: #c0c4cc;
}
</style>

<style>
.line-conflict-modal {
    width: 600px;
}

/* Ensure modal z-index is higher than dropdown */
.line-conflict-modal.el-message-box__wrapper {
    z-index: 3000 !important;
}

.line-conflict-modal {
    z-index: 3000 !important;
}

/* Ensure the message box itself also has high z-index */
.line-conflict-modal .el-message-box {
    z-index: 3001 !important;
}
</style>
